import React, { useEffect, useState } from 'react';

import Button from 'components/buttons/Button';
import Input from 'components/inputs/Input';
import Select from 'components/inputs/Select';
import TextArea from 'components/inputs/TextArea';
import NavigationLink from 'components/links/NavigationLink';
import { RequiredString } from 'helpers/yupHelper';
import Spinner from 'components/Spinner/Spinner';
import {
  FlexSection,
  GridComponent,
  MarginButton,
  NetworkFlexContainer,
  FormWrapper,
  InputsWrapper,
  LabelForm
} from './Styled';
import SectionWrapper from 'pages/Layout/SectionWrapper';
import { FieldErrors, UseFormMethods } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { AwardData } from 'services/rest/Award/createAward';
import { getGroupsList } from 'services/rest/Group/getGroupsList';
import * as yup from 'yup';
import UploadFileCropper from 'components/inputs/UploadFileCropper';
import Calendar from 'components/Calendar';
import Icon from 'components/icons/Icon';

export interface CreateAwardFormProps {
  onSubmit: (event: React.FormEvent) => void;
  errors: FieldErrors<AwardData>;
  register: UseFormMethods['register'];
  handleImage: any;
  setGroupsIds: any;
  loader: any;
  handleClick?: any;
}

export const userSchema = yup.object().shape({
  groupsIds: RequiredString(),
  description: RequiredString().max(1000)
});

const CreateAwardForm: React.FC<CreateAwardFormProps> = props => {
  const {
    errors,
    onSubmit,
    register,
    handleImage,
    setGroupsIds,
    loader,
    handleClick
  } = props;
  const { t } = useTranslation(['dates', 'awards_section', 'group_page']);
  const { currentUser } = useSelector(selectUser);
  const [groups, setGroups] = useState([]);
  const [filter, setFilter] = useState('month');
  const [value, setValue] = useState('');
  const [selectedGroup, setSelectedGroup] = useState([]);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedValue, setSelectedValue] = useState('');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [checked, setChecked] = useState({
    month: true,
    quarter: false,
    year: false,
    custom: false
  });
  const getData = async () => {
    const { data } = await getGroupsList(currentUser?.token, 100, 1);
    setGroups(data.data.docs);
  };

  useEffect(() => {
    getData();
  }, []);

  const year = [
    { text: new Date().getFullYear(), value: new Date().getFullYear() },
    { text: new Date().getFullYear() + 1, value: new Date().getFullYear() + 1 },
    { text: new Date().getFullYear() + 2, value: new Date().getFullYear() + 2 },
    { text: new Date().getFullYear() + 3, value: new Date().getFullYear() + 3 }
  ];

  const selectGroups = groups.map((group: any) => {
    return { text: group.name, value: group.id };
  });

  selectGroups.sort((a, b) => {
    return a.value - b.value;
  });

  const currentDate = new Date();

  const defaultCurrentTime = (param: any) => {
    const date = new Date();
    const currentTime = date.toLocaleTimeString('en-US', { hour12: false });
    const splitTime = currentTime.split(':');
    return param == 'hour' ? parseInt(splitTime[0]) : parseInt(splitTime[1]);
  };

  const monthsData = t('dates:month', {
    returnObjects: true,
    defaultValue: []
  });

  const months = Array.isArray(monthsData)
    ? monthsData.map((month: string, index: number) => ({
        text: month,
        value: index + 1
      }))
    : [];

  const currentMonth = currentDate.getMonth() + 1; // 1-12 format
  const availableMonths = months.slice(currentMonth - 1); // array is 0-based

  const datesQuarterData = t('dates:quarter', {
    returnObjects: true,
    defaultValue: []
  });

  const datesQuarter = Array.isArray(datesQuarterData)
    ? datesQuarterData.map((quarter, index) => ({
        text: quarter,
        value: index + 1
      }))
    : [];

  const currentQuarter = Math.floor(currentMonth / 3);
  const quarter = datesQuarter.slice(currentQuarter - 1);

  const onSelectedPeriodType = (periodType: string) => {
    switch (periodType) {
      case 'month':
        setChecked({
          month: true,
          quarter: false,
          year: false,
          custom: false
        });
        break;
      case 'quarter':
        setChecked({
          month: false,
          quarter: true,
          year: false,
          custom: false
        });
        break;
      case 'year':
        setChecked({
          month: false,
          quarter: false,
          year: true,
          custom: false
        });
        break;
      case 'custom':
        setChecked({
          month: false,
          quarter: false,
          year: false,
          custom: true
        });
        break;
      default:
        break;
    }
    setFilter(periodType);
    setSelectedValue('');
    setSelectedYear('');
  };

  const PeriodType = ({ options, setState, state, name }: any): JSX.Element => {
    return (
      <Select
        options={options}
        value={state}
        onSelect={e => e}
        placeholder={t('awards_section:select_an_option')}
        name={name}
        data-testid={name}
        register={register}
        onChange={e => {
          setState(e.target.value);
        }}
        error={errors.periodValue?.message}
        required={true}
      />
    );
  };

  const onSelectGroup = (event: any) => {
    const groupId = [];
    if (event.target.options[event.target.selectedIndex].text == 'ALL') {
      selectGroups.flatMap((group: any) => {
        groupId.push(group.value);
      });
    } else {
      groupId.push(event.target.value);
    }
    setGroupsIds(groupId);
    setSelectedGroup(event.target.value);
  };

  const onSelectCalendar = (startDate: Date, endDate: Date) => {
    if (startDate == null) {
      setStartDate(startDate);
    }

    if (endDate == null) {
      setEndDate(endDate);
    }

    handleClick(
      startDate,
      endDate,
      defaultCurrentTime('hour'),
      defaultCurrentTime('minutes')
    );
  };

  return (
    <SectionWrapper>
      <NavigationLink
        text={t('group_page:back')}
        direction="back"
        to={'/admin/award'}
      />
      <h1>{t('awards_section:create_award')}</h1>
      <form onSubmit={onSubmit}>
        <FormWrapper>
          <InputsWrapper>
            <LabelForm>
              <div>
                <label>{t('awards_section:award')}</label>
              </div>
              <div>
                <TextArea
                  name="description"
                  ref={register}
                  rows={8}
                  value={value}
                  error={errors.description?.message}
                  onChange={(event: any) =>
                    setValue((event.target as HTMLTextAreaElement).value)
                  }
                  data-testid="description"
                />
              </div>
              <div className="marginFile">
                <UploadFileCropper
                  name="photoUrl"
                  handleImage={handleImage}
                  maxFileSize={2000}
                  ref={register}
                  infoBox={t('awards_section:file_info_box')}
                />
              </div>
            </LabelForm>
            <LabelForm>
              <div>
                <label>{t('group_page:groups')}</label>
              </div>
              <div>
                <Select
                  options={selectGroups}
                  value={selectedGroup}
                  name="groupsIds"
                  data-testid="groupsIds"
                  placeholder={t('awards_section:select_an_option')}
                  register={register}
                  onChange={onSelectGroup}
                  error={errors.groupsIds?.message}
                  className="group-select"
                />
              </div>
            </LabelForm>
            <LabelForm>
              <div>
                <label>{t('awards_section:period')}</label>
              </div>
              <div className="network">
                <NetworkFlexContainer>
                  <label>{t('dates:period_type.month')}</label>
                  <Input
                    type="checkbox"
                    checked={checked.month}
                    id="month"
                    name="month"
                    onChange={() => onSelectedPeriodType('month')}
                  />
                </NetworkFlexContainer>
                <NetworkFlexContainer>
                  <label>{t('dates:period_type.quarter')}</label>
                  <Input
                    type="checkbox"
                    checked={checked.quarter}
                    id="quarter"
                    name="quarter"
                    onChange={() => onSelectedPeriodType('quarter')}
                  />
                </NetworkFlexContainer>
                <NetworkFlexContainer>
                  <label>{t('dates:period_type.year')}</label>
                  <Input
                    type="checkbox"
                    checked={checked.year}
                    id="year"
                    name="year"
                    onChange={() => onSelectedPeriodType('year')}
                  />
                </NetworkFlexContainer>
                <NetworkFlexContainer>
                  <label>{t('dates:period_type.custom')}</label>
                  <Input
                    type="checkbox"
                    checked={checked.custom}
                    id="custom"
                    name="custom"
                    onChange={() => onSelectedPeriodType('custom')}
                  />
                </NetworkFlexContainer>
              </div>
            </LabelForm>
            <LabelForm>
              <Input
                type="text"
                value={filter}
                name="periodType"
                ref={register}
                error={errors.periodType?.message}
                data-testid="periodType"
                style={{ display: 'none' }}
              />
              <div>
                <label>{t('awards_section:period_selection')}</label>
              </div>
              <div>
                {checked.month && (
                  <>
                    <PeriodType
                      options={availableMonths}
                      setState={setSelectedValue}
                      state={selectedValue}
                      name="periodValue"
                    />
                    <PeriodType
                      options={year}
                      setState={setSelectedYear}
                      state={selectedYear}
                      name="periodYear"
                    />
                  </>
                )}
                {checked.quarter && (
                  <>
                    <PeriodType
                      options={quarter}
                      setState={setSelectedValue}
                      state={selectedValue}
                      name="periodValue"
                    />
                    <PeriodType
                      options={year}
                      setState={setSelectedYear}
                      state={selectedYear}
                      name="periodYear"
                    />
                  </>
                )}
                {checked.year && (
                  <PeriodType
                    options={year}
                    setState={setSelectedValue}
                    state={selectedValue}
                    name="periodValue"
                  />
                )}
                {checked.custom && (
                  <GridComponent>
                    <FlexSection className="notranslate calendar-container">
                      <Icon size="big" name="custom-calendar" color="#9b9b9b" />
                      <Calendar
                        onChange={onSelectCalendar}
                        startDate={currentDate}
                        defaultStartDate={startDate}
                        defaultEndDate={endDate}
                        withinSameMonth={false}
                      />
                    </FlexSection>
                  </GridComponent>
                )}
              </div>
            </LabelForm>
            {!loader ? (
              <MarginButton>
                <Button
                  value={t('awards_section:create_award')}
                  variant="primary"
                  loader={loader}
                />
              </MarginButton>
            ) : (
              <Spinner name="sign-up" />
            )}
          </InputsWrapper>
        </FormWrapper>
      </form>
    </SectionWrapper>
  );
};
export default CreateAwardForm;
